import { useCallback, useEffect, useRef } from 'react';
import { useRecoilCallback } from 'recoil';
import { nanoid } from 'nanoid';
import { generationStateAtom, escapeHatchDegradedAtom } from './state';
import { isLadderExhausted, proposeBump, BACKOFF_CEILING, type GenerationState } from './policy';
import {
  updateRegistry,
  countEligible,
  OBSERVER_TTL_MS,
  type ObserverEntry,
  type ObserverRegistry,
} from './registry';
import { evaluateBump } from './decision';
import analytics from 'modules/analytics';
import { viewer } from 'modules/analytics/constants';
import { OutboundMessageTypes } from 'modules/viewer/workers/pixelDataSharedWorkerTypes';

// VX-4302 #5b: the automatic ladder trigger. This observer counts consecutive failed connection
// recreates (delivery never restored — VX-4284 withholds the worker's pong on a delivery stall, so
// the watchdog recreate fires and, if delivery stays dead, fires again). At N it becomes
// ladder-exhausted and votes; presence + standing votes are shared cross-tab over a dedicated native
// BroadcastChannel; when quorum (min(2, eligible)) of exhausted observers agree and the cooldown
// allows, it commits a generation bump on the durable atom (which then drives the reconnect and
// worker self-retirement). All of the math is the tested pure policy/registry/decision; this hook is
// the thin adapter that feeds events in and commits the decision out.

const COORDINATION_CHANNEL = 'pixel-worker-escape-hatch-coordination';

// One identity per browsing context (tab), stable across reloads via sessionStorage. The registry
// is keyed by this rather than the `/viewer/{id}` slot, which buys three properties at once: two
// tabs on the same slot count as two distinct observers (quorum is honest); multiple hook instances
// in one tab collapse to a single observer (no double-counting); and a reload reuses the SAME id, so
// the tab overwrites its own pre-reload entry instead of stranding a phantom exhausted vote that a
// peer would keep counting for the full TTL. sessionStorage is per-tab and survives reload but not
// tab close — exactly this lifetime.
const TAB_ID_STORAGE_KEY = 'viewer.escapeHatch.tabId';
const resolveTabId = (): string => {
  try {
    const existing = sessionStorage.getItem(TAB_ID_STORAGE_KEY);
    if (existing != null && existing !== '') return existing;
    const fresh = nanoid();
    sessionStorage.setItem(TAB_ID_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // sessionStorage can throw (privacy mode / disabled storage) — fall back to a per-context id.
    return nanoid();
  }
};
const TAB_ID = resolveTabId();

// Continuous delivery must hold for this long before we wind the escalation (backoffLevel/lastBumpAt)
// back down — longer than one recreate cycle so a flapping outage does not reset the backoff on every
// brief blip.
const BACKOFF_RESET_DWELL_MS = 2 * 60_000;

// Presence/vote channel. The key is constant, so we open it once at the file level and leave it open
// for the life of the document — it is a trivial resource and there is no benefit to tearing it down
// and reopening it per mount. Created lazily so importing this module never touches BroadcastChannel
// in an environment that lacks it.
let coordinationChannel: BroadcastChannel | null = null;
const getCoordinationChannel = (): BroadcastChannel | null => {
  if (coordinationChannel == null && typeof BroadcastChannel !== 'undefined') {
    coordinationChannel = new BroadcastChannel(COORDINATION_CHANNEL);
  }
  return coordinationChannel;
};

interface CoordinationMessage {
  origin: string;
  entry: ObserverEntry;
}

type ObservableWorker = {
  port: Pick<MessagePort, 'addEventListener' | 'removeEventListener'>;
} | null;

// A frame counts as delivery whether it arrives standalone or wrapped in a `batched-messages`
// envelope (the common case on the hot path). Compared against the worker's own enum so a renamed
// frame type is caught at compile time rather than silently looking like a stall.
const isFrameMessage = (type: unknown): boolean =>
  type === OutboundMessageTypes.HandleFrame || type === OutboundMessageTypes.HandleInitialFrame;

const isFrameDelivery = (data: unknown): boolean => {
  const msg = data as { type?: string; messages?: Array<{ type?: string } | null> } | null;
  if (msg == null) return false;
  if (isFrameMessage(msg.type)) return true;
  if (msg.type === OutboundMessageTypes.BatchedMessages && Array.isArray(msg.messages)) {
    return msg.messages.some((m) => isFrameMessage(m?.type));
  }
  return false;
};

// Decide whether to bump right now and, if so, the next generation. Pure — the caller commits it.
function planBump(
  state: GenerationState,
  registry: ObserverRegistry,
  now: number
): GenerationState | null {
  return evaluateBump({ generationState: state, registry, now }) ? proposeBump(state, now) : null;
}

export interface EscapeHatchCoordination {
  // Called by the watchdog each time it recreates the connection without restoring delivery.
  reportFailedRecreate: () => void;
}

export function useEscapeHatchCoordination({
  matchingWorker,
  enabled,
}: {
  matchingWorker: ObservableWorker;
  enabled: boolean;
}): EscapeHatchCoordination {
  // Genuine local mutable state (not Recoil mirrors): the per-page failed-recreate counter, this
  // tab's view of the cross-tab registry, and the pending sustained-delivery dwell timer.
  const failedRecreateRef = useRef(0);
  const registryRef = useRef<ObserverRegistry>({});
  const backoffResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Record our own entry in the local registry and broadcast it to peers. Takes the generation
  // explicitly so it holds no Recoil subscription; callers read it from their snapshot.
  const publishEntry = useCallback(
    (generation: number, over: Pick<ObserverEntry, 'hasDemand' | 'exhausted'>, now: number) => {
      const entry: ObserverEntry = { tabId: TAB_ID, generation, lastSeen: now, ...over };
      registryRef.current = updateRegistry(registryRef.current, entry, now);
      getCoordinationChannel()?.postMessage({ origin: TAB_ID, entry } as CoordinationMessage);
    },
    []
  );

  // Commit a bump if the registry + cooldown warrant one. Reads and writes Recoil through the
  // callback's snapshot/set, so there is no ref mirror of the atom.
  const commitBumpIfWarranted = useRecoilCallback(
    ({ snapshot, set }) =>
      (now: number) => {
        const state = snapshot.getLoadable(generationStateAtom).getValue();
        const next = planBump(state, registryRef.current, now);
        if (next == null) return;
        set(generationStateAtom, next);
        failedRecreateRef.current = 0;
        // Attention ladder: once bumps reach the ceiling and delivery is still down, surface the
        // ambient indicator. It clears on the next delivered frame (see observeFrame).
        if (next.backoffLevel >= BACKOFF_CEILING) set(escapeHatchDegradedAtom, true);
        analytics.track(viewer.sys.escapeHatchBump, {
          reason: 'ladder-exhausted',
          generation: next.generation,
          eligibleObservers: countEligible(registryRef.current, now, OBSERVER_TTL_MS),
        });
      },
    []
  );

  const reportFailedRecreate = useRecoilCallback(
    ({ snapshot, set }) =>
      () => {
        if (!enabled) return;
        const now = Date.now();
        failedRecreateRef.current += 1;
        // Delivery is interrupted again — cancel any pending sustained-delivery backoff reset.
        if (backoffResetTimerRef.current != null) {
          clearTimeout(backoffResetTimerRef.current);
          backoffResetTimerRef.current = null;
        }
        const state = snapshot.getLoadable(generationStateAtom).getValue();
        const exhausted = isLadderExhausted(failedRecreateRef.current);
        publishEntry(state.generation, { hasDemand: true, exhausted }, now);
        // Surface the indicator on ANY exhausted tab once the lineage has reached the ceiling —
        // including one that only adopted the bumped generation from a peer (it never runs the commit
        // path's ceiling branch, so without this its radiologist would see no indicator).
        if (exhausted && state.backoffLevel >= BACKOFF_CEILING) set(escapeHatchDegradedAtom, true);
        commitBumpIfWarranted(now);
      },
    [enabled, publishEntry, commitBumpIfWarranted]
  );

  // A peer's entry just arrived: fold it into the registry and re-evaluate, since it may newly
  // satisfy quorum.
  const handleIncoming = useCallback(
    (entry: ObserverEntry) => {
      const now = Date.now();
      registryRef.current = updateRegistry(registryRef.current, entry, now);
      commitBumpIfWarranted(now);
    },
    [commitBumpIfWarranted]
  );

  // A delivered frame (standalone or batched) means delivery is healthy again.
  const observeFrame = useRecoilCallback(
    ({ snapshot, set }) =>
      () => {
        const state = snapshot.getLoadable(generationStateAtom).getValue();
        const now = Date.now();
        // Once we have escalated (backoffLevel > 0), require a sustained run of healthy delivery
        // before winding the escalation back to 0. The timer is that proof: any fresh stall cancels
        // it (in reportFailedRecreate), so it only fires after BACKOFF_RESET_DWELL_MS of
        // uninterrupted frames — at which point the cooldown/indicator basis is safe to reset and a
        // later, unrelated incident starts from a clean slate rather than an inflated backoff.
        if (state.backoffLevel > 0 && backoffResetTimerRef.current == null) {
          backoffResetTimerRef.current = setTimeout(() => {
            backoffResetTimerRef.current = null;
            set(generationStateAtom, (s) =>
              s.backoffLevel === 0 && s.lastBumpAt === 0
                ? s
                : { ...s, backoffLevel: 0, lastBumpAt: 0 }
            );
          }, BACKOFF_RESET_DWELL_MS);
        }
        // Frames stream fast, so skip the publish in the steady state: nothing to clear (no failures,
        // not degraded) and peers already hold a current-generation entry for us. We still publish
        // once after a reconnect (the generation changed, so our prior entry is stale and would
        // inflate peers' eligible count for the whole TTL) and on a recovery transition.
        const degraded = snapshot.getLoadable(escapeHatchDegradedAtom).getValue();
        const ownEntryIsCurrent = registryRef.current[TAB_ID]?.generation === state.generation;
        if (failedRecreateRef.current === 0 && !degraded && ownEntryIsCurrent) return;
        failedRecreateRef.current = 0;
        if (degraded) set(escapeHatchDegradedAtom, false); // delivery restored — clear the indicator
        publishEntry(state.generation, { hasDemand: true, exhausted: false }, now);
      },
    [publishEntry]
  );

  // Cross-tab presence + votes over the shared coordination channel. Ignore our own echoes.
  useEffect(() => {
    if (!enabled) return;
    const channel = getCoordinationChannel();
    if (channel == null) return;
    const onMessage = (event: MessageEvent) => {
      const msg = event.data as CoordinationMessage | null;
      if (msg == null || msg.origin === TAB_ID) return;
      handleIncoming(msg.entry);
    };
    channel.addEventListener('message', onMessage as EventListener);
    return () => channel.removeEventListener('message', onMessage as EventListener);
  }, [enabled, handleIncoming]);

  // Delivery observation on the active worker port.
  useEffect(() => {
    if (!enabled || matchingWorker == null) return;
    const { port } = matchingWorker;
    const onMessage = (event: MessageEvent) => {
      if (isFrameDelivery(event.data)) observeFrame();
    };
    port.addEventListener('message', onMessage as EventListener);
    return () => {
      port.removeEventListener('message', onMessage as EventListener);
      if (backoffResetTimerRef.current != null) {
        clearTimeout(backoffResetTimerRef.current);
        backoffResetTimerRef.current = null;
      }
    };
  }, [enabled, matchingWorker, observeFrame]);

  return { reportFailedRecreate };
}
